import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type {
  BaseProps,
  ComponentDensity,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import Button from '../../atoms/buttons/button';

interface PaginationLabels {
  /** Accessible label for the previous-page action. */
  previous: string;
  /** Accessible label for the next-page action. */
  next: string;
  /** Full page-position label. */
  page: string;
  /** Compact page-position label. */
  compactPage: string;
}

interface PaginationProps extends BaseProps {
  /** One-based current page number. */
  currentPage: number;
  /** Total available page count. */
  totalPages: number;
  /** Called with the requested one-based page number. */
  onPageChange: (page: number) => void;
  /** Optional consumer overrides for the localized shared labels. */
  labels?: Partial<PaginationLabels>;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  variant = 'neutral' as ComponentVariant,
  size = 'md',
  className = '',
  appearance = 'soft',
  density = 'default' as ComponentDensity,
  labels,
}) => {
  const { t } = useI18n();
  const gapSizeClasses: Record<ComponentSize, string> = {
    lg: 'gap-6',
    md: 'gap-4',
    sm: 'gap-3',
    xs: 'gap-2',
    '2xs': 'gap-2',
  };
  const compactGapSizeClasses: Record<ComponentSize, string> = {
    lg: 'gap-4',
    md: 'gap-3',
    sm: 'gap-2',
    xs: 'gap-2',
    '2xs': 'gap-1.5',
  };

  const iconSizeClasses: Record<ComponentSize, string> = {
    lg: 'h-5 w-5',
    md: 'h-4 w-4',
    sm: 'h-4 w-4',
    xs: 'h-3 w-3',
    '2xs': 'h-3 w-3',
  };

  const baseClasses = [
    'flex',
    'justify-between',
    'items-center',
    density === 'compact' ? compactGapSizeClasses[size] : gapSizeClasses[size],
    className,
  ].join(' ');
  const resolvedLabels: PaginationLabels = {
    previous: labels?.previous ?? t('shared.pagination.previous'),
    next: labels?.next ?? t('shared.pagination.next'),
    page:
      labels?.page ??
      t('shared.pagination.page', {
        current: currentPage,
        total: totalPages,
      }),
    compactPage:
      labels?.compactPage ??
      t('shared.pagination.compactPage', {
        current: currentPage,
        total: totalPages,
      }),
  };

  return (
    <div className={baseClasses}>
      <Button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={resolvedLabels.previous}
        variant={variant}
        appearance={appearance}
        size={size}
      >
        <ChevronLeftIcon className={iconSizeClasses[size]} />
      </Button>
      <span className="text-sm">
        {density === 'compact'
          ? resolvedLabels.compactPage
          : resolvedLabels.page}
      </span>
      <Button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={resolvedLabels.next}
        variant={variant}
        appearance={appearance}
        size={size}
      >
        <ChevronRightIcon className={iconSizeClasses[size]} />
      </Button>
    </div>
  );
};

export default Pagination;
